import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { t, statusLabel, archetypeLabel } from "../lib/i18n";
import { Trophy, Sword, ArrowRight, Crown, Sparkle } from "@phosphor-icons/react";
import DeckOfWeekBanner from "../components/DeckOfWeekBanner";

export default function Home() {
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [decks, setDecks] = useState([]);
  const [site, setSite] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/tournaments"),
      api.get("/leaderboard"),
      api.get("/decks"),
      api.get("/site/settings"),
    ]).then(([tt, p, d, s]) => {
      setTournaments(tt.data);
      setPlayers(p.data.slice(0, 5));
      setDecks(d.data.slice(0, 3));
      setSite(s.data || {});
    }).catch(() => {});
  }, []);

  const upcoming = tournaments.find((x) => x.status === "upcoming");
  // Iter 39 — every hero field is admin-editable via /admin -> Site Settings;
  // empty admin values fall back to the localized i18n defaults so the
  // frontpage never renders blank labels while an admin is mid-edit.
  const heroImage = site.hero_image_url
    || "https://images.pexels.com/photos/14266493/pexels-photo-14266493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
  const heroEyebrow = site.hero_eyebrow || t.heroEyebrow;
  const heroLine1 = site.hero_line1 || t.heroLine1;
  const heroLine2 = site.hero_line2 || t.heroLine2;
  const heroLine3 = site.hero_line3 || t.heroLine3;
  const heroSubtitle = site.hero_subtitle || t.heroSubtitle;
  const heroPrimaryLabel = site.hero_cta_primary_label || t.heroBtnTournaments;
  const heroPrimaryUrl = site.hero_cta_primary_url || "/tournaments";
  const heroSecondaryLabel = site.hero_cta_secondary_label || t.heroBtnDecks;
  const heroSecondaryUrl = site.hero_cta_secondary_url || "/decks";

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url('${heroImage}')` }}
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/85 to-[#05050A]/40" />
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -right-20 top-10 hidden lg:block">
          <div className="spin-slow w-[420px] h-[420px] rounded-full border border-[#FF0055]/20" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-40">
          <div className="flex items-center gap-2 mb-5 fade-up">
            <span className="w-2 h-2 bg-[#FF0055] rounded-full animate-pulse" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
              {heroEyebrow}
            </span>
          </div>
          <h1
            className="font-display font-black uppercase italic leading-[0.85] text-6xl sm:text-7xl lg:text-[8rem] fade-up"
            style={{ animationDelay: "60ms" }}
            data-testid="hero-title"
          >
            {heroLine1} <span className="text-[#FF0055]">{heroLine2}</span>
            <br />
            <span className="text-stroke">{heroLine3}</span>
          </h1>
          <p
            className="mt-6 max-w-xl text-lg text-white/70 fade-up"
            style={{ animationDelay: "120ms" }}
            data-testid="hero-subtitle"
          >
            {heroSubtitle}
          </p>
          <div
            className="mt-10 flex flex-wrap items-center gap-4 fade-up"
            style={{ animationDelay: "180ms" }}
          >
            <Link
              to={heroPrimaryUrl}
              data-testid="hero-cta-tournaments"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF0055] text-white font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#FF3377] transition-all duration-200 glow-pink"
            >
              <Trophy size={18} weight="fill" /> {heroPrimaryLabel}
            </Link>
            <Link
              to={heroSecondaryUrl}
              data-testid="hero-cta-decks"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white font-bold uppercase tracking-[0.2em] text-sm hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
            >
              <Sword size={18} weight="fill" /> {heroSecondaryLabel}
            </Link>
          </div>

          {upcoming && (
            <Link
              to={`/tournaments/${upcoming.id}`}
              className="mt-12 inline-flex items-center gap-3 group fade-up"
              style={{ animationDelay: "240ms" }}
              data-testid="featured-tournament-link"
            >
              <span className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] font-mono font-bold">
                {t.heroFeatured}
              </span>
              <span className="text-sm text-white/80 group-hover:text-white">
                {upcoming.name} · {upcoming.location} · {t.heroPrize} {upcoming.prize_pool}
              </span>
              <ArrowRight size={14} className="text-[#00E5FF]" />
            </Link>
          )}
        </div>
      </section>

      {/* Iter 41 — the stats strip previously here (Active / Bladers /
          Decks / Prize) was showing zeros on the freshly-purged site.
          Swap it for the Deck of the Week voting banner so the space
          drives engagement instead. */}
      <DeckOfWeekBanner />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <SectionHeader eyebrow={t.sectionCircuit} title={t.sectionTournaments} link="/tournaments" />
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {tournaments.slice(0, 2).map((tt) => (
            <TournamentBigCard key={tt.id} tt={tt} />
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <SectionHeader eyebrow={t.sectionMeta} title={t.sectionFeaturedDecks} link="/decks" />
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {decks.map((d) => (
              <Link
                key={d.id}
                to={`/decks/${d.id}`}
                data-testid={`deck-card-${d.id}`}
                className="group border border-white/10 bg-[#0C0C14] p-5 hover:border-[#FF0055]/60 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF0055]">
                    {archetypeLabel(d.archetype)}
                  </span>
                  <Sparkle size={14} className="text-white/30" />
                </div>
                <div className="font-display font-black uppercase italic text-2xl mt-3 group-hover:text-[#00E5FF] transition-colors">
                  {d.name}
                </div>
                <p className="text-xs text-white/50 mt-2 line-clamp-2">{d.description}</p>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <SectionHeader eyebrow={t.sectionRankings} title={t.sectionTopBladers} link="/leaderboard" />
          <div className="mt-8 border border-white/10 bg-[#0C0C14]">
            {players.map((p, i) => (
              <Link
                key={p.id}
                to={`/players/${p.id}`}
                data-testid={`top-player-${p.id}`}
                className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 border-white/5 hover:bg-white/5"
              >
                <span className="font-mono text-xs text-white/40 w-6">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {i === 0 && <Crown size={14} weight="fill" className="text-[#E2FF00]" />}
                <span className="flex-1 text-sm font-semibold">{p.handle}</span>
                <span className="font-mono text-sm font-bold text-[#00E5FF]">{p.points}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ eyebrow, title, link }) {
  return (
    <div className="flex items-end justify-between border-b border-white/10 pb-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">// {eyebrow}</div>
        <h2 className="font-display font-black uppercase italic text-4xl mt-2">{title}</h2>
      </div>
      {link && (
        <Link to={link} className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-[#00E5FF] flex items-center gap-2">
          {t.viewAll} <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}

function TournamentBigCard({ tt }) {
  const isLive = tt.status === "live";
  const isUpcoming = tt.status === "upcoming";
  return (
    <Link
      to={`/tournaments/${tt.id}`}
      data-testid={`home-tournament-${tt.id}`}
      className="group relative block overflow-hidden border border-white/10 hover:border-[#FF0055]/60 transition-colors"
    >
      <div className="aspect-[16/9] bg-cover bg-center" style={{ backgroundImage: `url('${tt.banner_url}')` }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/60 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span className={`text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 ${
          isLive ? "bg-[#FF0055] text-white"
          : isUpcoming ? "border border-[#00E5FF] text-[#00E5FF]"
          : "border border-white/30 text-white/60"
        }`}>
          {statusLabel(tt.status)}
        </span>
        <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/60">
          {tt.format === "group_knockout" ? t.formatGroupKnockout : t.formatSingleElim}
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="font-display font-black uppercase italic text-3xl group-hover:text-[#00E5FF] transition-colors">
          {tt.name}
        </div>
        <div className="text-xs text-white/60 font-mono mt-2 flex flex-wrap gap-3">
          <span>{tt.location}</span>
          <span>·</span>
          <span>{new Date(tt.start_date).toLocaleDateString("pl-PL")}</span>
          <span>·</span>
          <span className="text-[#E2FF00]">{tt.prize_pool}</span>
        </div>
      </div>
    </Link>
  );
}
